import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Wallet, ArrowLeft, Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";

const QUICK_AMOUNTS = [500, 1000, 2000, 5000];

// Load Cashfree SDK based on env. Defaults to sandbox in mock mode.
const loadCashfreeSdk = () =>
  new Promise((resolve, reject) => {
    if (window.Cashfree) return resolve(window.Cashfree);
    const env = (process.env.REACT_APP_CASHFREE_ENV || "TEST").toUpperCase();
    const src =
      env === "PROD"
        ? "https://sdk.cashfree.com/js/v3/cashfree.js"
        : "https://sdk.cashfree.com/js/v3/cashfree.sandbox.js";
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => resolve(window.Cashfree);
    s.onerror = () => reject(new Error("Failed to load Cashfree SDK"));
    document.body.appendChild(s);
  });

export default function CustomerWalletTopup() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("");
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null); // {status, amount, newBalance}

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/v2/wallets/${user.id}/summary`);
      setSummary(r.data);
    } catch (e) {
      toast.error("Failed to load wallet");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const startTopup = async () => {
    const amt = Number(amount);
    if (!amt || amt < 100) {
      toast.error("Minimum top-up is ₹100");
      return;
    }
    setProcessing(true);
    setResult(null);
    try {
      const initR = await api.post(`/v2/payments/wallet/initiate`, {
        customer_id: user.id,
        amount: amt,
      });
      const orderId = initR.data?.cashfree_order_id;
      const sessionId = initR.data?.payment_session_id;
      const isMocked = !!initR.data?.mocked;

      if (isMocked || !sessionId) {
        // Mocked path: go straight to verify
        const verR = await api.post(`/v2/payments/wallet/verify`, {
          cashfree_order_id: orderId,
        });
        const st = verR.data?.status || "SUCCESS";
        setResult({
          status: st,
          amount: amt,
          newBalance: verR.data?.new_balance,
        });
        if (st === "SUCCESS") {
          toast.success(`₹${amt} added to wallet (mock)`);
          load();
          refreshUser();
        }
      } else {
        // Real Cashfree
        const Cashfree = await loadCashfreeSdk();
        const cfMode =
          (process.env.REACT_APP_CASHFREE_ENV || "TEST").toUpperCase() === "PROD"
            ? "production"
            : "sandbox";
        const cashfree = Cashfree({ mode: cfMode });
        const checkoutOpts = {
          paymentSessionId: sessionId,
          redirectTarget: "_modal",
        };
        const cfRes = await cashfree.checkout(checkoutOpts);
        if (cfRes.error) {
          setResult({ status: "FAILED", error: cfRes.error.message });
        } else {
          const verR = await api.post(`/v2/payments/wallet/verify`, {
            cashfree_order_id: orderId,
          });
          const st = verR.data?.status || "PENDING";
          setResult({
            status: st,
            amount: amt,
            newBalance: verR.data?.new_balance,
          });
          if (st === "SUCCESS") { load(); refreshUser(); }
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Top-up failed");
      setResult({ status: "FAILED", error: e.message });
    } finally {
      setProcessing(false);
    }
  };

  const currentBal = Number(summary?.current_balance || 0);
  const newBal = currentBal + Number(amount || 0);

  return (
    <DashboardLayout>
      <div className="space-y-5 max-w-2xl mx-auto" data-testid="customer-wallet-topup">
        <button
          onClick={() => navigate("/customer/wallet")}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"
          data-testid="back-to-wallet-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Wallet
        </button>

        <div>
          <h1 className="text-2xl font-bold text-slate-800">Add Money to Wallet</h1>
          <p className="text-sm text-slate-500">
            Top-up your wallet to book new shipments instantly
          </p>
        </div>

        {/* Current Balance card */}
        <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 rounded-xl p-5 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase font-bold text-emerald-700 tracking-wider">
              Current Balance
            </div>
            <div
              className={`text-3xl font-bold mt-1 ${
                currentBal >= 0 ? "text-emerald-700" : "text-red-700"
              }`}
              data-testid="current-balance"
            >
              {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : `₹${currentBal.toFixed(2)}`}
            </div>
          </div>
          {summary?.wallet_type && (
            <span className="text-[10px] font-bold px-2 py-1 rounded border bg-white text-emerald-700 border-emerald-300">
              {summary.wallet_type}
            </span>
          )}
        </div>

        {!result && (
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5">
            <div>
              <div className="text-xs font-bold uppercase text-slate-500 mb-2">
                Quick Amounts
              </div>
              <div className="grid grid-cols-4 gap-2">
                {QUICK_AMOUNTS.map((a) => (
                  <button
                    key={a}
                    onClick={() => setAmount(String(a))}
                    className={`border rounded-md py-2 text-sm font-semibold transition ${
                      Number(amount) === a
                        ? "bg-slate-800 text-white border-slate-800"
                        : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                    }`}
                    data-testid={`quick-amount-${a}`}
                  >
                    ₹{a}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs font-bold uppercase text-slate-500 mb-1.5">
                Custom Amount
              </div>
              <Input
                type="number"
                min="100"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount (min ₹100)"
                data-testid="custom-amount-input"
              />
              {Number(amount) > 0 && (
                <p className="text-xs text-slate-500 mt-1.5">
                  After top-up:{" "}
                  <span className="font-semibold text-emerald-700">
                    ₹{newBal.toFixed(2)}
                  </span>
                </p>
              )}
            </div>

            <Button
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={startTopup}
              disabled={processing || !amount || Number(amount) < 100}
              data-testid="pay-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing…
                </>
              ) : (
                <>
                  <Wallet className="w-4 h-4 mr-2" /> Pay ₹{amount || 0}
                </>
              )}
            </Button>
            <p className="text-[11px] text-slate-400 text-center">
              Secured by Cashfree Payments. PCI-DSS compliant.
            </p>
          </div>
        )}

        {result && <ResultPanel result={result} onClose={() => { setResult(null); setAmount(""); }} onBack={() => navigate("/customer/wallet")} />}
      </div>
    </DashboardLayout>
  );
}

function ResultPanel({ result, onClose, onBack }) {
  const { status, amount, newBalance, error } = result;
  if (status === "SUCCESS") {
    return (
      <div
        className="bg-emerald-50 border border-emerald-300 rounded-xl p-6 text-center"
        data-testid="topup-success"
      >
        <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600 mb-2" />
        <h3 className="text-xl font-bold text-emerald-800">
          ₹{Number(amount || 0).toFixed(2)} added successfully!
        </h3>
        {newBalance != null && (
          <p className="text-slate-700 mt-2">
            New balance:{" "}
            <span className="font-bold">₹{Number(newBalance).toFixed(2)}</span>
          </p>
        )}
        <div className="flex justify-center gap-2 mt-4">
          <Button onClick={onBack} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="back-to-wallet-success">
            Back to Wallet
          </Button>
          <Button variant="outline" onClick={onClose} data-testid="topup-again">
            Top-up again
          </Button>
        </div>
      </div>
    );
  }
  if (status === "FAILED") {
    return (
      <div
        className="bg-red-50 border border-red-300 rounded-xl p-6 text-center"
        data-testid="topup-failed"
      >
        <XCircle className="w-12 h-12 mx-auto text-red-600 mb-2" />
        <h3 className="text-xl font-bold text-red-800">Payment failed</h3>
        <p className="text-slate-700 mt-2 text-sm">
          {error || "Please try again."}
        </p>
        <Button onClick={onClose} className="mt-4 bg-slate-800 hover:bg-slate-900 text-white">
          Try Again
        </Button>
      </div>
    );
  }
  return (
    <div
      className="bg-amber-50 border border-amber-300 rounded-xl p-6 text-center"
      data-testid="topup-pending"
    >
      <Clock className="w-12 h-12 mx-auto text-amber-600 mb-2" />
      <h3 className="text-xl font-bold text-amber-800">Payment pending</h3>
      <p className="text-slate-700 mt-2 text-sm">
        We&apos;ll update your wallet once the payment is confirmed.
      </p>
      <Button onClick={onBack} className="mt-4 bg-slate-800 hover:bg-slate-900 text-white">
        Back to Wallet
      </Button>
    </div>
  );
}

